import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import client from "@/lib/api";
import { logger } from "@/lib/logger";

export default function WaitlistModal({ open, onClose, prefEmail, list = "premium" }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState(prefEmail || "");
  const [status, setStatus] = useState("form"); // form → success
  const [alreadyOn, setAlreadyOn] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setEmail(prefEmail || ""); }, [prefEmail]);
  useEffect(() => {
    if (!open) {
      setStatus("form"); setFirstName(""); setAlreadyOn(false); setLoading(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await client.post("/waitlist", { email, first_name: firstName, list });
      setAlreadyOn(!!data.already_on_list);
      setStatus("success");
    } catch (err) {
      logger.error("Waitlist join failed:", err);
      toast.error("Could not join waitlist. Please try again.");
    }
    setLoading(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="waitlist-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-mizizi-charcoal/70 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md bg-mizizi-cream text-mizizi-charcoal shadow-2xl border border-mizizi-gold/40">
        <button
          type="button"
          onClick={onClose}
          data-testid="waitlist-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full hover:bg-mizizi-charcoal/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 md:p-10">
          <div className="overline text-mizizi-terracotta">Premium Experience · Waitlist</div>
          <h3 className="font-serif text-3xl mt-3 leading-tight">The Mizizi Table Experience</h3>
          <p className="mt-3 text-sm text-mizizi-charcoal/70 leading-relaxed">
            The premium bundle unlocks the moment every element is ready — the recorded cooking session,
            the hosting cards, and the high-resolution print-ready cookbook PDF. Join the list and you'll be
            first through the door.
          </p>

          {status === "form" && (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="overline text-mizizi-charcoal/70 block mb-1" htmlFor="wl-name">First name</label>
                <input
                  id="wl-name" required minLength={1} value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="waitlist-first-name"
                  className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta"
                />
              </div>
              <div>
                <label className="overline text-mizizi-charcoal/70 block mb-1" htmlFor="wl-email">Email address</label>
                <input
                  id="wl-email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="waitlist-email"
                  className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit"
                className="w-full pill pill-primary justify-center disabled:opacity-60"
              >
                {loading ? "Adding you…" : "Join the premium waitlist"}
              </button>
              <p className="text-[11px] text-mizizi-charcoal/50 text-center pt-2">
                No charge. We'll email you the moment the full experience is ready.
              </p>
            </form>
          )}

          {status === "success" && (
            <div className="mt-8 text-center" data-testid="waitlist-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-mizizi-gold/20 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-mizizi-gold" />
              </div>
              <p className="mt-6 font-serif text-2xl">
                {alreadyOn ? "Already on the list." : "You're in."}
              </p>
              <p className="mt-3 text-sm text-mizizi-charcoal/70 leading-relaxed">
                {alreadyOn
                  ? "You joined the premium waitlist earlier — we'll write when the experience opens."
                  : `Karibu, ${firstName || "friend"}. Watch your inbox — we'll be in touch when the full experience opens.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
